"use client";

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { initializePostStats } from "@/lib/actions/postStats";

interface PostStatsContextValue {
  slug: string;
  viewCount: number | null;
  likeCount: number;
  isLiked: boolean;
  isInitializing: boolean;
  setLikeState: (isLiked: boolean, likeCount: number) => void;
}

const PostStatsContext = createContext<PostStatsContextValue | null>(null);

export function PostStatsProvider({
  slug,
  children,
}: {
  slug: string;
  children: ReactNode;
}) {
  const [viewCount, setViewCount] = useState<number | null>(null);
  const [likeCount, setLikeCount] = useState(0);
  const [isLiked, setIsLiked] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  useEffect(() => {
    let cancelled = false;

    void initializePostStats(slug)
      .then((result) => {
        if (cancelled || !result.success) return;
        setViewCount(result.viewCount > 0 ? result.viewCount : null);
        setLikeCount(result.likeCount);
        setIsLiked(result.isLiked);
      })
      .catch((error) => {
        console.error("Failed to initialize post stats:", error);
      })
      .finally(() => {
        if (!cancelled) setIsInitializing(false);
      });

    return () => {
      cancelled = true;
    };
  }, [slug]);

  const setLikeState = useCallback((liked: boolean, count: number) => {
    setIsLiked(liked);
    setLikeCount(count);
  }, []);

  const value = useMemo(
    () => ({
      slug,
      viewCount,
      likeCount,
      isLiked,
      isInitializing,
      setLikeState,
    }),
    [slug, viewCount, likeCount, isLiked, isInitializing, setLikeState]
  );

  return (
    <PostStatsContext.Provider value={value}>
      {children}
    </PostStatsContext.Provider>
  );
}

export function usePostStats(): PostStatsContextValue {
  const value = useContext(PostStatsContext);
  if (!value) {
    throw new Error("usePostStats must be used within PostStatsProvider");
  }
  return value;
}
